import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, Plus, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTIONS = { majorations: "Majorations produits pro (%)", auto: "Auto", moto: "Moto", rc_decennale: "RC Décennale",
  multirisque_pro: "Multirisque Pro", habitation: "Multirisque Habitation" };
const LABELS = {
  rc_decennale: "RC Décennale (%)", rc_decennale_ca_seuil: "Seuil CA RC Décennale (€)", rc_decennale_ca_eleve: "RC Décennale au-delà du seuil (%)",
  multirisque_pro: "Multirisque Pro (%)", cybersecurite: "Cyber-sécurité (%)", base: "Base annuelle (€)", bonus_min: "Bonus minimum",
  bonus_max: "Coefficient max avant rappel", frais_dossier: "Frais de dossier (€)", mois_a_payer: "Mois payés à la souscription", experience_seuil_annees: "Seuil expérience (années)",
  experience_coef_debutant: "Coef. sous le seuil", experience_coef_confirme: "Coef. au-dessus du seuil", activites: "Métiers (id · libellé · base €)",
  ca_tranches: "Tranches de CA (jusqu'à € · coef)", surface_tranches: "Tranches de surface (jusqu'à m² · coef)",
  valeur_tranches: "Tranches matériel + stock (jusqu'à € · coef)", mobilier_tranches: "Tranches mobilier (jusqu'à € · coef)",
  base_appartement: "Base appartement (€)", base_maison: "Base maison (€)", coef_locataire: "Coef. locataire",
  coef_proprietaire_occupant: "Coef. propriétaire occupant", coef_proprietaire_non_occupant: "Coef. propriétaire non occupant",
  supplement_par_piece: "Supplément par pièce (€)", coef_rez_de_chaussee: "Coef. rez-de-chaussée", coef_etage: "Coef. étage",
  jusqu_a: "Jusqu'à", coef: "Coef.", id: "Identifiant", label: "Libellé",
  base_locataire_appartement: "Base locataire appartement (€)", base_locataire_maison: "Base locataire maison (€)",
  base_proprietaire_occupant_appartement: "Base propriétaire occupant appartement (€)", base_proprietaire_occupant_maison: "Base propriétaire occupant maison (€)",
  base_proprietaire_non_occupant_appartement: "Base propriétaire non occupant appartement (€)", base_proprietaire_non_occupant_maison: "Base propriétaire non occupant maison (€)",
  coef_logement_etudiant: "Coef. logement étudiant (sur locataire appartement)", surface_appartement_tranches: "Surface appartement (jusqu'à m² · coef)",
  surface_maison_tranches: "Surface maison (jusqu'à m² · coef)", formules: "Formules (id · libellé · coef)", frais_dossier_inclus: "Frais de dossier inclus dans le prix (€)",
};
const pretty = (k) => LABELS[k] || k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const toVal = (s) => (s === "" ? null : isNaN(Number(s)) ? s : Number(s));

const Field = ({ label, value, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 text-sm">
    <span className="text-slate-600">{label}</span>
    <Input value={value ?? ""} onChange={(e) => onChange(toVal(e.target.value))} className="w-40 h-9" data-testid={testid} />
  </label>
);

const ListEditor = ({ rows, onChange, testid }) => {
  const keys = rows.length ? Object.keys(rows[0]) : ["jusqu_a", "coef"];
  const update = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 items-center">
          {keys.map((k) => (
            <Input key={k} value={r[k] ?? ""} placeholder={pretty(k)} title={pretty(k)} onChange={(e) => update(i, k, toVal(e.target.value))} className="h-9" />
          ))}
          <Button variant="ghost" size="sm" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, Object.fromEntries(keys.map((k) => [k, null]))])}>
        <Plus className="w-4 h-4 mr-1" /> Ajouter une ligne
      </Button>
      <p className="text-xs text-slate-400">Colonnes : {keys.map(pretty).join(" · ")} — « jusqu'à » vide = au-delà ; coef vide = rappel conseiller</p>
    </div>
  );
};

export const PricingEditor = () => {
  const [grids, setGrids] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/pricing`).then((r) => setGrids(r.data)).catch(() => {}); }, []);
  const setIn = (section, key, v) => setGrids((g) => ({ ...g, [section]: { ...g[section], [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/pricing`, grids);
      setGrids(res.data);
      toast.success("Grilles tarifaires enregistrées");
    } catch (e) {
      toast.error("Enregistrement impossible");
    }
    setSaving(false);
  };

  if (!grids) return null;
  return (
    <Card className="border-0 shadow-sm" data-testid="pricing-editor-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-[#102692]" /> Grilles tarifaires</CardTitle>
          <CardDescription>Bases, coefficients, majorations et frais de dossier des calculateurs</CardDescription>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#102692]" data-testid="pricing-save-btn"><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
      </CardHeader>
      <CardContent className="grid lg:grid-cols-2 gap-6">
        {Object.entries(SECTIONS).map(([section, title]) => (
          <div key={section} className="p-4 rounded-xl bg-slate-50 space-y-3" data-testid={`pricing-section-${section}`}>
            <h3 className="font-semibold text-slate-800">{title}</h3>
            {Object.entries(grids[section] || {}).map(([k, v]) => Array.isArray(v) ? (
              <div key={k}>
                <p className="text-sm text-slate-600 mb-1">{pretty(k)}</p>
                <ListEditor rows={v} onChange={(rows) => setIn(section, k, rows)} testid={`pricing-${section}-${k}`} />
              </div>
            ) : (
              <Field key={k} label={pretty(k)} value={v} onChange={(nv) => setIn(section, k, nv)} testid={`pricing-${section}-${k}`} />
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
